<script setup lang="ts">
import { computed, onMounted, ref, toRefs } from "vue";
import Button from "@/stories/buttons/Button/Button.vue";
import SelectInput from "@/stories/form/SelectInput/SelectInput.vue";
import { DAYS_IN_WEEK } from "@/consts";
import { useI18n } from "vue-i18n";
import TextInput from "@/stories/form/TextInput/TextInput.vue";
import Checkbox from "@/stories/form/Checkbox/Checkbox.vue";
import { useBreakpoints } from "@vueuse/core";
import { BREAKPOINTS } from "@/consts";
import { useRRuleOptions } from "@/composables/useRRuleOptions";
import type {
  PrimitiveModel,
  RepeatRuleEndingTypes,
  RruleOptions,
} from "@/types";
import getDefaultRRules from "@/helpers/rrules/getDefaultRRules";
import Line from "@/stories/layout/Line/Line.vue";
import joi from "joi";
import { validate } from "@/helpers/forms/validate";
import type {
  IMultiSelectItem,
  IOption,
  IValidationResult,
} from "@/interfaces";
import BigDialog from "@/stories/dialogs/BigDialog.vue";
import DialogHeader from "@/stories/dialogs/DialogHeader.vue";
import DialogContent from "@/stories/dialogs/DialogContent.vue";
import DialogFooterActions from "@/stories/dialogs/DialogFooterActions.vue";
import HorizontalForm from "@/stories/form/HorizontalForm/HorizontalForm.vue";
import { usePlannerTemplateRepeats } from "@/composables/usePlannerTemplateRepeats";
import TagSelectorInput from "@/stories/form/TagSelectorInput/TagSelectorInput.vue";
import FormGroup from "@/stories/form/FormGroup/FormGroup.vue";
import DatePicker from "@/stories/form/DatePicker/DatePicker.vue";
import { toUnix } from "@/stories/utils/dates";
import { addDays } from "date-fns";

interface Props {
  initialValue?: Partial<RruleOptions>;
}

const props = defineProps<Props>();

const emit = defineEmits<{ close: []; update: [RruleOptions] }>();

const { initialValue } = toRefs(props);

const { t } = useI18n();
const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = breakpoints.smaller("tablet");

const { repeatRuleOptions } = usePlannerTemplateRepeats();

const getNextEndingDate = () => toUnix(addDays(new Date(), 7));

const rruleToSave = ref<RruleOptions>({
  ...getDefaultRRules(),
  endingType: "finish_on",
  endingDate: getNextEndingDate(),
  ...initialValue.value,
});
const validation = ref<IValidationResult | null>();

onMounted(() => {
  setMonthlyValues();
});

const { typeString } = useRRuleOptions(rruleToSave);

const VALIDATION_SCHEMA = joi.object({
  repeatAmount: joi.number().required().min(1).max(99),
  occurence: joi.number().required().min(1),
});

const handleSave = () => {
  validation.value = validate(VALIDATION_SCHEMA, {
    ...rruleToSave.value,
  });

  if (validation.value.isValid) {
    emit("update", rruleToSave.value);
  }
};

const ORDER_LIST = computed<IOption[]>(() => {
  return [
    {
      value: 1,
      title: t("templates.rruleinput.first"),
    },
    {
      value: 2,
      title: t("templates.rruleinput.second"),
    },
    {
      value: 3,
      title: t("templates.rruleinput.third"),
    },
    {
      value: 4,
      title: t("templates.rruleinput.fourth"),
    },
    {
      value: -1,
      title: t("templates.rruleinput.last"),
    },
  ];
});

const DAYS_LIST = computed<IOption[]>(() => {
  return [
    { value: 0, title: t("calendar.days.monday") },
    { value: 1, title: t("calendar.days.tuesday") },
    { value: 2, title: t("calendar.days.wednesday") },
    { value: 3, title: t("calendar.days.thursday") },
    { value: 4, title: t("calendar.days.friday") },
    { value: 5, title: t("calendar.days.saturday") },
    { value: 6, title: t("calendar.days.sunday") },
  ];
});

const setMonthlyValues = () => {
  if (rruleToSave.value.cadence === 30) {
    rruleToSave.value.dayOrder = 1;
    rruleToSave.value.daySelection = 0;
  } else {
    rruleToSave.value.dayOrder = undefined;
    rruleToSave.value.daySelection = undefined;
  }
};

const handleCadenceUpdate = (value: PrimitiveModel) => {
  rruleToSave.value.cadence = value as number;
  setMonthlyValues();
};

const endOptions: IMultiSelectItem<RepeatRuleEndingTypes>[] = [
  {
    id: "finish_on",
    title: t("templates.rruleinput.finish_on"),
  },
  {
    id: "finish_after",
    title: t("templates.rruleinput.finish_after"),
  },
  {
    id: "no_end_date",
    title: t("templates.no_end_date"),
  },
];
</script>

<template>
  <BigDialog size="xs" @on-close="emit('close')">
    <DialogHeader @close="$emit('close')">
      {{ $t("global.recurrence") }}
    </DialogHeader>

    <DialogContent>
      <FormGroup>
        <SelectInput
          name="repeat_cadence"
          :label="t('global.recurrence')"
          :items="repeatRuleOptions"
          :modelValue="rruleToSave.cadence"
          :autoDeselect="false"
          @update:modelValue="handleCadenceUpdate"
        />

        <div v-if="rruleToSave.cadence === 30">
          <HorizontalForm class="tablet:items-end">
            <SelectInput
              name="dayOrder"
              :label="t('templates.nth_date')"
              :items="ORDER_LIST"
              :autoDeselect="false"
              v-model="rruleToSave.dayOrder"
            />
            <SelectInput
              name="daySelection"
              :items="DAYS_LIST"
              :autoDeselect="false"
              v-model="rruleToSave.daySelection"
            />
          </HorizontalForm>
        </div>

        <TextInput
          name="occurence"
          :number-only="true"
          :label="t('templates.rruleinput.every')"
          type="text"
          v-model="rruleToSave.occurence"
          :label-right="typeString"
          :validation="validation"
        />

        <div class="flex justify-between" v-if="rruleToSave.cadence === 7">
          <Checkbox
            v-for="(day, index) in Object.keys(rruleToSave.days)"
            :key="index"
            name="daySelection"
            :text="$t(DAYS_IN_WEEK[index]!).slice(0, 3)"
            :is-stacked="isMobile"
            v-model="rruleToSave.days[index]"
          />
        </div>

        <Line padding="xs" />

        <TagSelectorInput
          name="endingType"
          size="sm"
          :items="endOptions"
          :allowDeselect="false"
          v-model="rruleToSave.endingType"
        />

        <DatePicker
          v-if="rruleToSave.endingType === 'finish_on'"
          name="startDate"
          is-required
          v-model="rruleToSave.endingDate"
        />

        <TextInput
          v-if="rruleToSave.endingType === 'finish_after'"
          name="repeatAmount"
          :number-only="true"
          type="text"
          v-model="rruleToSave.repeatAmount"
          :label-right="
            [1, 7].includes(rruleToSave.cadence)
              ? $t('templates.amounts')
              : $t('repeats.repetitions')
          "
          :validation="validation"
        />
      </FormGroup>
    </DialogContent>

    <DialogFooterActions>
      <Button
        type="white"
        :text="$t('global.cancel')"
        alignment="center"
        @click="emit('close')"
      />

      <Button type="primary" :text="$t('global.save')" @click="handleSave" />
    </DialogFooterActions>
  </BigDialog>
</template>
